import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Breadcrumbs from '../components/breadcrumbs'
import Footer from '../components/footer'
import { getCanonicalUrl } from '../utils/canonical'
import './sms-compliance.css'

const SmsCompliance = () => {
  useEffect(() => {
    window.scrollTo(0, 0)

    if (typeof AOS !== 'undefined') {
      AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: false,
        offset: 100,
        delay: 0,
        mirror: true
      })
    }
  }, [])

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'SMS Compliance' }
  ]

  return (
    <div className="sms-compliance-container">
      <Helmet>
        <title>SMS Compliance & Opt-In Policy | DataFlows Australia</title>
        <link rel="canonical" href={getCanonicalUrl('/sms-compliance')} />
        <meta
          name="description"
          content="DataFlows SMS compliance policy for Australian businesses. Covers Spam Act 2003 requirements, ACMA guidelines, opt-in consent, opt-out (STOP), sender identification, and how DataFlows clients must comply."
        />
        <meta name="keywords" content="SMS compliance Australia, Spam Act 2003, ACMA SMS compliance, SMS opt-in Australia, SMS opt-out, STOP keyword, Australian SMS law, SMS consent, commercial electronic message" />
        <meta property="og:title" content="SMS Compliance & Opt-In Policy | DataFlows Australia" />
        <meta property="og:description" content="DataFlows SMS compliance policy under the Australian Spam Act 2003 and ACMA guidelines. Covers opt-in consent, opt-out handling, and sender identification requirements." />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "url": "https://dataflows.com.au/sms-compliance",
          "name": "SMS Compliance & Opt-In Policy",
          "description": "DataFlows SMS compliance policy for Australian businesses under the Spam Act 2003 and ACMA guidelines.",
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dataflows.com.au/" },
              { "@type": "ListItem", "position": 2, "name": "SMS Compliance", "item": "https://dataflows.com.au/sms-compliance" }
            ]
          },
          "publisher": {
            "@type": "Organization",
            "name": "DataFlows SMS",
            "url": "https://dataflows.com.au"
          }
        })}</script>
      </Helmet>

      {/* Breadcrumbs */}
      <div className="compliance-breadcrumbs">
        <div className="container">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      {/* Hero Section */}
      <section className="compliance-hero">
        <div className="container">
          <div className="hero-content" data-aos="fade-up">
            <div className="hero-badge">Spam Act 2003 Compliant</div>
            <h1>SMS Compliance &amp; Opt-In Policy</h1>
            <p className="hero-subtitle">
              DataFlows is committed to responsible SMS messaging. This page describes how end-users consent to receive messages, how to opt out, and how we comply with Australian law — including the Spam Act 2003 and ACMA guidelines.
            </p>
            <div className="last-updated">
              <span>Last updated: February 2025</span>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Reference Banner */}
      <section className="compliance-quick-ref">
        <div className="container">
          <div className="quick-ref-grid" data-aos="fade-up">
            <div className="quick-ref-item">
              <div className="quick-ref-label">Opt-In</div>
              <div className="quick-ref-value">Explicit consent via client's signup form</div>
            </div>
            <div className="quick-ref-item">
              <div className="quick-ref-label">Opt-Out</div>
              <div className="quick-ref-value">Reply <strong>STOP</strong> to any message</div>
            </div>
            <div className="quick-ref-item">
              <div className="quick-ref-label">Help</div>
              <div className="quick-ref-value">Reply <strong>HELP</strong> for support info</div>
            </div>
            <div className="quick-ref-item">
              <div className="quick-ref-label">Frequency</div>
              <div className="quick-ref-value">Varies by sender and service type</div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="compliance-content">
        <div className="container">
          <div className="content-wrapper">

            <div className="compliance-section" data-aos="fade-up">
              <h2>1. About DataFlows</h2>
              <p>
                DataFlows Australia Pty Ltd (ABN 23 685 656 893) is an SMS platform and reseller. We provide SMS messaging infrastructure to businesses ("Clients") who use our platform to communicate with their own customers and end-users ("End Users").
              </p>
              <p>
                DataFlows does not independently solicit end-users. All SMS communication is initiated by our Clients using their own opt-in processes, for which they are responsible.
              </p>
            </div>

            <div className="compliance-section" data-aos="fade-up">
              <h2>2. How End Users Opt In</h2>
              <p>
                End Users receive SMS messages from DataFlows Clients <strong>only after providing explicit prior consent</strong>. Consent is collected by the Client directly — typically via one of the following methods:
              </p>
              <ul>
                <li>A web form or landing page where the user provides their phone number and agrees to receive SMS</li>
                <li>A checkout or account registration page with a clearly displayed SMS consent checkbox</li>
                <li>A verbal or written agreement at the point of service</li>
                <li>A keyword opt-in where the user texts a keyword to a number to subscribe</li>
              </ul>
              <div className="compliance-note">
                <strong>Reseller Policy:</strong> DataFlows requires all Clients to confirm they have obtained proper, documented consent from End Users as a condition of using our platform. Clients who violate this requirement are subject to account suspension.
              </div>
            </div>

            <div className="compliance-section" data-aos="fade-up">
              <h2>3. Message Frequency</h2>
              <p>
                Message frequency varies depending on the Client and the nature of their service. End Users may receive:
              </p>
              <ul>
                <li>Transactional messages (order confirmations, delivery updates, appointment reminders)</li>
                <li>Informational messages (account alerts, verification codes, security notifications)</li>
                <li>Promotional messages (offers, campaigns) — only where explicit marketing consent has been obtained</li>
              </ul>
              <p>
                The frequency of messages is set by the individual Client and disclosed to End Users at the time of opt-in.
              </p>
            </div>

            <div className="compliance-section" data-aos="fade-up">
              <h2>4. How to Opt Out (STOP)</h2>
              <p>
                End Users can opt out of receiving SMS messages <strong>at any time</strong> by replying <strong>STOP</strong> to any message received from a DataFlows-powered number. Upon receipt of STOP:
              </p>
              <ul>
                <li>The opt-out is processed immediately</li>
                <li>No further messages will be sent to that number from that sender</li>
                <li>A single confirmation message may be sent acknowledging the opt-out</li>
              </ul>
              <p>
                Other supported opt-out keywords include: <strong>STOP</strong>, <strong>STOPALL</strong>, <strong>UNSUBSCRIBE</strong>, <strong>CANCEL</strong>, <strong>END</strong>, and <strong>QUIT</strong>.
              </p>
              <div className="compliance-note highlight">
                <strong>To opt out:</strong> Reply <strong>STOP</strong> to any message you have received. You will receive a confirmation and no further messages will be sent.
              </div>
            </div>

            <div className="compliance-section" data-aos="fade-up">
              <h2>5. Help &amp; Support</h2>
              <p>
                End Users who need assistance can:
              </p>
              <ul>
                <li>Reply <strong>HELP</strong> to any message to receive support contact information</li>
                <li>Email us at <a href="mailto:support@dataflows.com.au" className="compliance-link">support@dataflows.com.au</a></li>
                <li>Visit our website at <a href="https://dataflows.com.au" className="compliance-link">dataflows.com.au</a></li>
              </ul>
            </div>

            <div className="compliance-section" data-aos="fade-up">
              <h2>6. Message &amp; Data Rates</h2>
              <p>
                Message and data rates may apply depending on your mobile carrier and plan. DataFlows does not charge End Users for receiving messages — any charges are between the End User and their mobile carrier.
              </p>
            </div>

            <div className="compliance-section" data-aos="fade-up">
              <h2>7. Australian Law — Spam Act 2003 &amp; ACMA</h2>
              <p>
                DataFlows and its Clients operate in compliance with the <strong>Spam Act 2003 (Cth)</strong> and guidelines issued by the <strong>Australian Communications and Media Authority (ACMA)</strong>. The Spam Act prohibits sending unsolicited commercial electronic messages to Australian accounts, and sets out three core requirements:
              </p>
              <ul>
                <li><strong>Consent:</strong> Commercial SMS messages may only be sent with express, inferred, or deemed consent from the recipient. Express consent is the strongest and most clearly documented form.</li>
                <li><strong>Identification:</strong> Every commercial message must clearly identify the sending organisation and provide accurate contact information so recipients can identify who sent the message.</li>
                <li><strong>Unsubscribe:</strong> Every commercial message must include a functional and easy-to-use unsubscribe mechanism. Opt-out requests must be honoured within 5 business days.</li>
              </ul>
              <p>
                DataFlows is an ACMA-registered Originating Telco, listed on the official ACMA SMS Sender ID Register. All Clients are required to comply with the Spam Act 2003 as a condition of using our platform.
              </p>
              <div className="compliance-note">
                <strong>For ACMA and regulators:</strong> DataFlows operates as a reseller platform. Opt-in consent is collected by each Client at their respective customer touchpoints. DataFlows maintains platform-level compliance standards and requires Client attestation of consent collection. Please contact <a href="mailto:support@dataflows.com.au" className="compliance-link">support@dataflows.com.au</a> for compliance documentation requests.
              </div>
            </div>

            <div className="compliance-section" data-aos="fade-up">
              <h2>8. Privacy &amp; Data Handling</h2>
              <p>
                Phone numbers and personal data collected as part of the SMS opt-in process are handled in accordance with our Privacy Policy. We do not sell or share End User phone numbers with third parties for marketing purposes.
              </p>
              <p>
                <Link to="/privacy-policy" className="compliance-link">Read our Privacy Policy →</Link>
              </p>
            </div>

            <div className="compliance-section" data-aos="fade-up">
              <h2>9. Client Responsibilities</h2>
              <p>
                Businesses using the DataFlows platform ("Clients") are responsible for:
              </p>
              <ul>
                <li>Obtaining valid, documented opt-in consent from all End Users before sending messages</li>
                <li>Disclosing message frequency and nature of messages at the point of opt-in</li>
                <li>Providing clear opt-out instructions in every message</li>
                <li>Maintaining records of End User consent</li>
                <li>Complying with all applicable laws including the Australian Spam Act 2003, Privacy Act 1988, and ACMA guidelines</li>
                <li>Promptly honouring all opt-out requests</li>
              </ul>
              <p>
                Clients who fail to meet these standards are subject to immediate account suspension and may be reported to carriers or regulators.
              </p>
            </div>

            <div className="compliance-section" data-aos="fade-up">
              <h2>10. Contact &amp; Compliance Requests</h2>
              <p>
                For compliance documentation, carrier verification requests, or general enquiries about our SMS policies:
              </p>
              <div className="contact-info">
                <p><strong>DataFlows Australia Pty Ltd</strong></p>
                <p>ABN: 23 685 656 893</p>
                <p>Email: <a href="mailto:support@dataflows.com.au">support@dataflows.com.au</a></p>
                <p>Website: <a href="https://dataflows.com.au">https://dataflows.com.au</a></p>
                <p><Link to="/contact-us">Contact Us Form →</Link></p>
              </div>
            </div>

          </div>

          {/* Related Links */}
          <div className="compliance-related" data-aos="fade-up">
            <h3>Related Policies</h3>
            <div className="related-links">
              <Link to="/terms-of-use" className="related-link">Terms of Use</Link>
              <Link to="/privacy-policy" className="related-link">Privacy Policy</Link>
              <Link to="/contact-us" className="related-link">Contact Us</Link>
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  )
}

export default SmsCompliance
